// Radar (feature catalogue F7/I2 "Work Radar", X4 portfolio radar): the cards of all boards on one
// calendar-week timeline (GET /api/radar), grouped by board, by domain (lane) or by person, filtered
// by people, domains, status and kind of board (project/team, doc 13). Rows by board or domain
// show the lanes' load per week against their capacity (X5). Read-only; a card opens its board on
// it. The settings live in the URL, so a radar view can be shared as a link.
import { isoWeek, mondayOf, weekKey, weekRange } from "./iso";
import { STATUS, STICKY, css, loadColor } from "./palette";
import { formatPoints } from "./iso";

export interface RadarRow {
  board: string; boardTitle: string; id: string; title: string; owner: string; assignees: string[];
  status: number; color: number; week: string; inCell: boolean; lane: string; frame: string;
  due: string | null; points: number | null;
}
/** Load of the lanes with one title on one board in one week (server radar.rs RadarLoad). */
export interface RadarLoad { board: string; boardTitle: string; lane: string; week: string; points: number; cards: number; capacity: number | null }
interface RadarList { weeks: string[]; rows: RadarRow[]; loads?: RadarLoad[]; people: string[]; lanes: string[] }
type Group = "board" | "lane" | "person";
type Kind = "" | "project" | "team";
const KINDS: Record<Kind, string> = { "": "Alle Boards", project: "Projekte", team: "Teams" };

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const ORDER = [1, 4, 2, 3];
const KEY = "lanewerk.radar.status";
const GROUPS: Record<Group, string> = { board: "Board", lane: "Domäne", person: "Person" };
const SPANS = [6, 12, 26];

export { weekKey };
/** "2026-W43" moved by n weeks (via the Thursday, so no time zone can shift the week). */
export function shiftWeek(key: string, n: number): string {
  const [y, w] = key.split("-W").map(Number);
  const thu = new Date(mondayOf(y, w).getTime() + (3 + 7 * n) * 864e5);
  const r = isoWeek(new Date(thu.getUTCFullYear(), thu.getUTCMonth(), thu.getUTCDate()));
  return weekKey(r.year, r.week);
}

export class Radar {
  private from: string;
  private span: number;
  private group: Group;
  private people: Set<string>;
  private lanes: Set<string>;
  private statuses: Set<number>;
  private kind: Kind;
  private data: RadarList = { weeks: [], rows: [], people: [], lanes: [] };
  private readonly today = isoWeek(new Date());

  constructor() {
    document.title = "Radar – Lanewerk";
    const q = new URLSearchParams(location.search);
    const now = weekKey(this.today.year, this.today.week);
    this.from = /^\d{4}-W\d{2}$/.test(q.get("from") ?? "") ? q.get("from")! : now;
    this.span = SPANS.includes(Number(q.get("weeks"))) ? Number(q.get("weeks")) : 12;
    this.group = (q.get("group") as Group) in GROUPS ? (q.get("group") as Group) : "board";
    // empty choice = everyone / every domain
    this.people = new Set((q.get("people") ?? "").split(",").map((p) => p.trim().toUpperCase()).filter(Boolean));
    this.lanes = new Set((q.get("lanes") ?? "").split("\n").map((l) => l.trim()).filter(Boolean));
    this.statuses = new Set(readStatuses());
    const k = q.get("kind");
    this.kind = k === "project" || k === "team" ? k : "";

    $("#lw-radar-prev").addEventListener("click", () => { this.from = shiftWeek(this.from, -Math.max(1, Math.floor(this.span / 2))); void this.load(); });
    $("#lw-radar-next").addEventListener("click", () => { this.from = shiftWeek(this.from, Math.max(1, Math.floor(this.span / 2))); void this.load(); });
    $("#lw-radar-today").addEventListener("click", () => { this.from = now; void this.load(); });
    const span = $<HTMLSelectElement>("#lw-radar-span");
    span.replaceChildren(...SPANS.map((n) => { const o = el("option", undefined, `${n} Wochen`); o.value = String(n); o.selected = n === this.span; return o; }));
    span.addEventListener("change", () => { this.span = Number(span.value); void this.load(); });
    const groups = $("#lw-radar-group");
    for (const [g, label] of Object.entries(GROUPS) as [Group, string][]) {
      const b = el("button", "chip plain", label);
      b.type = "button";
      b.dataset.group = g;
      b.addEventListener("click", () => { this.group = g; this.keepUrl(); this.render(); });
      groups.appendChild(b);
    }
    const chips = $("#lw-radar-status");
    for (const s of ORDER) {
      const b = el("button", "chip", STATUS[s].label);
      b.type = "button";
      b.style.setProperty("--dot", css(STATUS[s].rgb));
      b.dataset.status = String(s);
      b.addEventListener("click", () => {
        if (this.statuses.has(s)) this.statuses.delete(s); else this.statuses.add(s);
        writeStatuses([...this.statuses]);
        this.render();
      });
      chips.appendChild(b);
    }
    const kinds = $("#lw-radar-kind");
    for (const [k, label] of Object.entries(KINDS) as [Kind, string][]) {
      const b = el("button", "chip plain", label);
      b.type = "button";
      b.dataset.kind = k;
      b.addEventListener("click", () => { this.kind = k; void this.load(); });
      kinds.appendChild(b);
    }
    void this.load();
  }

  private keepUrl() {
    const p = new URLSearchParams({ view: "radar", from: this.from, weeks: String(this.span), group: this.group, people: [...this.people].join(",") });
    if (this.lanes.size) p.set("lanes", [...this.lanes].join("\n"));
    if (this.kind) p.set("kind", this.kind);
    history.replaceState(null, "", `?${p}`);
  }

  async load() {
    this.keepUrl();
    const status = $("#lw-radar-error");
    const p = new URLSearchParams({ from: this.from, weeks: String(this.span), people: [...this.people].join(","), lanes: [...this.lanes].join("\n") });
    if (this.kind) p.set("kind", this.kind);
    try {
      const r = await fetch(`./api/radar?${p}`, { headers: { accept: "application/json" } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      this.data = (await r.json()) as RadarList;
      status.textContent = "";
    } catch (e) {
      status.textContent = `Der Radar konnte nicht geladen werden (${(e as Error).message}).`;
    }
    this.render();
  }

  /** People / domain chips: a click toggles one; "Alle" clears the choice. */
  private picker(box: HTMLElement, all: string[], chosen: Set<string>, allLabel: string) {
    const mk = (label: string, on: boolean, fn: () => void, title?: string) => {
      const b = el("button", "chip plain", label);
      b.type = "button";
      b.setAttribute("aria-pressed", String(on));
      if (title) b.title = title;
      b.addEventListener("click", fn);
      return b;
    };
    const items = [...new Set([...all, ...chosen])].sort((a, b) => a.localeCompare(b, "de"));
    box.replaceChildren(
      mk(allLabel, chosen.size === 0, () => { chosen.clear(); void this.load(); }),
      ...items.map((v) => mk(v, chosen.has(v), () => { if (chosen.has(v)) chosen.delete(v); else chosen.add(v); void this.load(); })),
    );
  }

  private render() {
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-radar-group [data-group]")) b.setAttribute("aria-pressed", String(b.dataset.group === this.group));
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-radar-status [data-status]")) b.setAttribute("aria-pressed", String(this.statuses.has(Number(b.dataset.status))));
    for (const b of document.querySelectorAll<HTMLButtonElement>("#lw-radar-kind [data-kind]")) b.setAttribute("aria-pressed", String((b.dataset.kind ?? "") === this.kind));
    this.picker($("#lw-radar-people"), this.data.people, this.people, "Alle Personen");
    this.picker($("#lw-radar-lanes"), this.data.lanes, this.lanes, "Alle Domänen");
    const weeks = this.data.weeks;
    const first = weeks[0], last = weeks[weeks.length - 1];
    $("#lw-radar-range").textContent = first ? `KW ${Number(first.split("-W")[1])} – KW ${Number(last.split("-W")[1])} · ${first.slice(0, 4)}${last.slice(0, 4) !== first.slice(0, 4) ? `/${last.slice(0, 4)}` : ""}` : "";
    const rows = this.data.rows.filter((r) => this.statuses.has(r.status));
    // groups: rows of the grid
    type G = { label: string; cards: { r: RadarRow; helping: boolean }[]; load: Map<string, { points: number; capacity: number }> };
    const groups = new Map<string, G>();
    const group = (key: string, label: string) => {
      let g = groups.get(key);
      if (!g) { g = { label, cards: [], load: new Map() }; groups.set(key, g); }
      return g;
    };
    const add = (key: string, label: string, r: RadarRow, helping = false) => group(key, label).cards.push({ r, helping });
    // load vs capacity (X5) per board or domain and week: lanes with a capacity only (free ones too,
    // unless people are chosen – then only rows with their cards)
    if (this.group !== "person") {
      for (const l of this.data.loads ?? []) {
        if (l.capacity == null) continue;
        const key = this.group === "board" ? l.board : l.lane.toLowerCase();
        if (this.people.size && !groups.has(key) && !rows.some((r) => (this.group === "board" ? r.board : r.lane.toLowerCase()) === key)) continue;
        const g = group(key, this.group === "board" ? l.boardTitle || l.board : l.lane || "Ohne Spur");
        const w = g.load.get(l.week) ?? { points: 0, capacity: 0 };
        w.points += l.points;
        w.capacity += l.capacity;
        g.load.set(l.week, w);
      }
    }
    for (const r of rows) {
      if (this.group === "board") add(r.board, r.boardTitle || r.board, r);
      else if (this.group === "lane") add(r.lane.toLowerCase(), r.lane || "Ohne Spur", r);
      else {
        add(r.owner || "", r.owner || "Ohne Verantwortliche", r);
        for (const a of r.assignees) add(a, a, r, true);
      }
    }
    const order = [...groups.entries()].sort((a, b) => (a[0] === "" ? 1 : b[0] === "" ? -1 : a[1].label.localeCompare(b[1].label, "de")));
    const grid = $("#lw-radar-grid");
    grid.style.setProperty("--weeks", String(weeks.length));
    const head = el("div", "radar-row radar-head");
    head.appendChild(el("div", "radar-label", GROUPS[this.group]));
    const now = weekKey(this.today.year, this.today.week);
    for (const w of weeks) {
      const [y, n] = w.split("-W").map(Number);
      const c = el("div", "radar-week");
      if (w === now) c.classList.add("now");
      c.append(el("strong", undefined, `KW ${n}`), el("span", undefined, weekRange(y, n)));
      head.appendChild(c);
    }
    const lines = order.map(([, g]) => {
      const line = el("div", "radar-row");
      const label = el("div", "radar-label", g.label);
      label.appendChild(el("span", "radar-n", String(g.cards.length)));
      line.appendChild(label);
      for (const w of weeks) {
        const cell = el("div", "radar-cell");
        if (w === now) cell.classList.add("now");
        const load = g.load.get(w);
        if (load) cell.appendChild(loadBar(load.points, load.capacity));
        for (const { r, helping } of g.cards.filter((c) => c.r.week === w)) cell.appendChild(this.chip(r, helping));
        line.appendChild(cell);
      }
      return line;
    });
    grid.replaceChildren(head, ...lines);
    const boards = new Set(rows.map((r) => r.board)).size;
    $("#lw-radar-count").textContent = `${rows.length} ${rows.length === 1 ? "Karte" : "Karten"} auf ${boards} ${boards === 1 ? "Board" : "Boards"}`;
    const empty = $("#lw-radar-empty");
    empty.hidden = rows.length > 0;
    empty.textContent = this.data.rows.length
      ? "Keine Karte mit diesem Status in diesen Wochen."
      : "In diesen Wochen ist nichts geplant – für die gewählten Personen und Domänen. Karten zählen für die Woche ihrer Zelle, sonst ihres Fälligkeitsdatums.";
  }

  private chip(r: RadarRow, helping: boolean): HTMLElement {
    const a = el("a", "radar-card");
    a.href = `?board=${encodeURIComponent(r.board)}&card=${encodeURIComponent(r.id)}`;
    a.style.setProperty("--card", css((STICKY[r.color] ?? STICKY[0]).rgb));
    a.style.setProperty("--dot", css((STATUS[r.status] ?? STATUS[1]).rgb));
    if (!r.inCell) a.classList.add("by-due");
    if (r.status === 2 || r.status === 3) a.classList.add("closed");
    a.appendChild(el("span", "radar-title", r.title || "Ohne Titel"));
    const meta: string[] = [];
    if (this.group !== "person" && r.owner) meta.push(r.owner);
    if (helping) meta.push("mitwirkend");
    if (this.group !== "board") meta.push(r.boardTitle || r.board);
    if (this.group !== "lane" && r.lane) meta.push(r.lane);
    if (r.points != null) meta.push(`${r.points} SP`);
    a.appendChild(el("span", "radar-meta", meta.join(" · ")));
    a.title = `${r.title}\n${[r.boardTitle, r.frame, r.lane].filter(Boolean).join(" · ")}${r.inCell ? "" : "\nnach Fälligkeit eingeplant"}`;
    return a;
  }
}

/** "8 / 10 SP" with a bar: green below 80 %, amber up to 100 %, red above. */
function loadBar(points: number, capacity: number): HTMLElement {
  const box = el("div", "radar-load");
  const ratio = capacity > 0 ? points / capacity : 0;
  box.style.setProperty("--load", css(loadColor(ratio)));
  box.style.setProperty("--fill", `${Math.min(100, Math.round(ratio * 100))}%`);
  if (ratio > 1) box.classList.add("over");
  const p = Math.round(points * 10) / 10, c = Math.round(capacity * 10) / 10;
  box.append(el("span", "radar-load-bar"), el("span", "radar-load-text", `${formatPoints(p)} / ${formatPoints(c)} SP`));
  box.title = `Verplant ${formatPoints(p)} von ${formatPoints(c)} Story Points (${Math.round(ratio * 100)} %), ohne abgebrochene Karten`;
  return box;
}

function readStatuses(): number[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (Array.isArray(v)) return v.filter((x) => ORDER.includes(x));
  } catch { /* storage blocked */ }
  return [1, 4, 2]; // open, waiting, done
}
function writeStatuses(v: number[]) {
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* not kept */ }
}
